"use client";

import React, { FC, useContext, useEffect, useState } from "react";
import { find } from "lodash";
import { Typography } from "@/components/Typography";
import { Wrapper } from "@/components/Wrapper";
import { PricingCard } from "@/components/PricingCard";
import { PlansManager } from "./PlansManager";
import { RichTextBlock } from "@/components/RichTextBlock";
import { BenefitsList } from "./BenefitsList";
import { RadioButton } from "@/components/RadioButton";
import { Chip } from "@/components/Chip";

import { UserContext } from "@/contexts/UserContext";

import { SubscriptionPlan } from "@/types/types";
import { ISubscriptionPlansContainer } from "./types";
import { PlanProps } from "@/types/plan";
import { isSubscriptionActive, isUserSubscribed } from "@/services/UserService";
import { AmplitudeContext } from "@/contexts/AmplitudeContext/AmplitudeContext";
import { amplitudeTrackEvent } from "@/services/AmplitudeService/AmplitudeService";
import { validatePricngEventSource } from "@/utils/qrEditorNavigatorHelper";

const mergePlansWithData = (
  plansList: PlanProps[],
  plansInfo: SubscriptionPlan[]
): SubscriptionPlan[] => {
  return plansInfo.map((plan) => {
    const pricingPlan = find(
      plansList,
      (currentPlan) => currentPlan.id === plan.id
    );
    const completedPlan = {
      ...plan,
      data: pricingPlan,
    };
    return completedPlan;
  });
};

export const SubscriptionPlansContainer: FC<ISubscriptionPlansContainer> = ({
  data,
  plans,
}) => {
  const userContext = React.useContext(UserContext);
  const { source, qrSelected } = React.useContext(AmplitudeContext);

  const plansInfo = data?.prices;

  const pricingPlans = mergePlansWithData(plans, plansInfo);

  const [currentPlan, setCurrentPlan] = useState<SubscriptionPlan>(
    find(pricingPlans, (plan) => plan.selected)
  );

  const onSelectPlan = (planId: string) => {
    const selectedPlan = find(pricingPlans, (plan) => plan.id === planId);
    setCurrentPlan(selectedPlan);
  };

  const isStatusActive =
    isUserSubscribed(userContext) && isSubscriptionActive(userContext);

  useEffect(() => {
    if (userContext) {
      userContext.setPrices(plans);
    }
    const sourceEv = { source: validatePricngEventSource(source) };

    amplitudeTrackEvent("view_pricing", {
      ...(validatePricngEventSource(source) && sourceEv),
      ...(qrSelected && { "qr type": qrSelected }),
    });
  }, []);

  return (
    <main>
      <PlansManager
        userContext={userContext}
        data={data}
        currentPlan={currentPlan}
      />
      <Wrapper>
        <div className="plan-wrapper">
          {plansInfo &&
            plansInfo.map((plan, idx) => (
              <PricingCard
                key={plan.id}
                active={plan.id === currentPlan?.id}
                header={plan.header}
                onClick={onSelectPlan}
                id={plan.id}
                dataQa={plan["data-qa"]}
                customClass={plan.customClass}
              >
                <div className="plan-box__title">
                  <div className="plan-box__name">
                    {!isStatusActive && (
                      <RadioButton
                        id={plan.id}
                        name="plan"
                        className="plan-box__title__radio"
                        value={plan.id}
                        checked={plan.id === currentPlan?.id}
                        readOnly={true}
                      />
                    )}
                    <RichTextBlock item={plan.name.richtext} />

                    {plan.name.chip && (
                      <Chip
                        label={plan.name.chip}
                        color={"warning"}
                        type={"filled"}
                        className={"button--chip"}
                        size="medium"
                      />
                    )}
                  </div>

                  <div className="plan-box__price">
                    {Array.isArray(plan.price.richtext) ? (
                      plan.price.richtext.map((item, ind) => (
                        <RichTextBlock item={item} key={ind} />
                      ))
                    ) : (
                      <RichTextBlock item={plan.price.richtext} />
                    )}
                  </div>
                </div>
                <div className={plan.benefits.richtext.classname}>
                  <BenefitsList
                    list={plan?.benefits?.richtext?.list}
                    active={plan?.id === currentPlan?.id}
                  />
                </div>
              </PricingCard>
            ))}
        </div>
      </Wrapper>
      <div className="dynamic-benefits">
        <div className="dynamic-benefits__wrapper">
          <BenefitsList
            list={currentPlan?.benefits?.richtext?.list}
            active={true}
          />
        </div>
      </div>
      <div className="phone-box__wrapper">
        <div className="u-text-center">
          {data.phone?.richtext?.map((phoneBoxItem, i) => {
            return (
              <Typography
                key={i}
                Tag={phoneBoxItem.tag}
                classname={phoneBoxItem.classname}
              >
                {phoneBoxItem.content}
              </Typography>
            );
          })}
        </div>
      </div>
    </main>
  );
};
